/**
 * «Когда» для отметок времени: время — только если это сегодня.
 *
 * Голое `HH:MM` для записи трёхдневной давности читается как сегодняшнее.
 * Заметка тренера «09:42 · план обновлён» выглядела свежей независимо от
 * того, когда её написали; то же самое было с временем последней
 * синхронизации устройства.
 *
 * Слова («вчера», «25 июл») даёт `Intl` по локали — своих ключей не нужно.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/**
 * @param iso     метка времени с бэкенда
 * @param locale  BCP-47 (см. `lib/dateLocale.ts`)
 * @param now     точка отсчёта — параметр, чтобы тест не зависел от часов
 * @returns       `"14:00"` сегодня · `"вчера, 14:00"` вчера ·
 *                `"25 июл, 14:00"` раньше · `null`, если метки нет
 */
export function formatWhen(
  iso: string | null | undefined,
  locale = "ru-RU",
  now: Date = new Date(),
): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  const time = d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  const days = Math.round((startOfDay(now) - startOfDay(d)) / DAY_MS);

  if (days === 0) return time;
  if (days === 1 || days === -1) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
    return `${rtf.format(-days, "day")}, ${time}`;
  }
  const date = d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  return `${date}, ${time}`;
}
